import * as React from "react";

import { useAnalytics } from "@/providers/analytics";
import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { DataSourceSelector } from "@/shared/components/visualization/controls/DataSourceSelector";
import { isViewableAsRawMessage } from "@/shared/components/visualization/Panel/predicates";
import { AddIcon } from "@/shared/foundation/icons/mui";
import {
  actions,
  RawMessagePanelState,
  useVizDispatch,
} from "@/shared/state/visualization";

import styles from "./AddMessagePath.module.css";
import { useIsDuplicateMessagePath } from "./useIsDuplicateMessagePath";

interface AddMessagePathProps {
  state: RawMessagePanelState;
}

export function AddMessagePath({ state }: AddMessagePathProps) {
  const vizStateDispatch = useVizDispatch();
  const { trackEvent } = useAnalytics();
  const [isAdding, setIsAdding] = React.useState(false);

  const isExisting = useIsDuplicateMessagePath(state.data);

  const handleCancel = () => {
    setIsAdding(false);
  };

  if (isAdding) {
    return (
      <div className={styles.formContainer}>
        <RobotoTypography variant="overline">New message path</RobotoTypography>
        <div className={styles.form}>
          <DataSourceSelector
            onChange={(messagePathNode) => {
              vizStateDispatch(
                actions.addDataToRawMessagePanel(
                  state.id,
                  messagePathNode.toTopicData(),
                ),
              );
              trackEvent("AddMessagePathCompleted");
              setIsAdding(false);
            }}
            optionFilter={(messagePathNode) => {
              if (isExisting(messagePathNode.toTopicData())) {
                return false;
              }
              if (
                state.data.length > 0 &&
                messagePathNode.topic.id !== state.data[0].topic.id
              ) {
                return false;
              }
              return isViewableAsRawMessage(messagePathNode);
            }}
          />

          <div className={styles.positionRight}>
            <RobotoButton
              eventName={"AddMessagePathCanceled"}
              onClick={handleCancel}
              size="small"
              type="button"
            >
              Cancel
            </RobotoButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RobotoButton
      eventName={"AddMessagePathClicked"}
      onClick={() => setIsAdding(true)}
      size="small"
      startIcon={<AddIcon />}
    >
      Add Message Path
    </RobotoButton>
  );
}
